/**
 * The anonymous shop-browse funnel (ADR-0076).
 *
 * Separate from `showAnalytics.ts` by design, not for filing: a Show-scoped id joins two steps that
 * happen on different pages minutes apart, while everything measured here happens inside one page
 * view. Reusing the Show wrapper would also inherit its null-`showId` branch, which reuses a saved
 * Show id and would therefore carry a Show attendee's identity into an unrelated shop visit.
 */

type EventProperties = Record<string, boolean | number | string>;

export type ShopAnalyticsSink = {
  capture(event: string, properties: EventProperties): void;
};

/** Why a scanned label found nothing to show. `sold` is the case TRO-483's empty state speaks to;
 * `not_here` is a label from a different shop, or an item never published to this book. The two
 * want different fixes, so a single "miss" count would be the wrong number. */
export type ScanMissCause = "sold" | "not_here";

export function createShopAnalytics(sink: ShopAnalyticsSink) {
  return {
    /** The page loaded. `has_scan` is the denominator that makes every other event a rate: a
     * visit that arrived from a label QR is a different population from one that followed a
     * shared link, and averaging them hides whichever is smaller. */
    shopOpened(shopSlug: string, hasScan: boolean, itemCount: number, referrerClass: string) {
      sink.capture("shop_opened", {
        shop_slug: shopSlug,
        has_scan: hasScan,
        item_count: itemCount,
        referrer_class: referrerClass,
      });
    },
    buylistViewed(shopSlug: string, source: "show" | "qr" | "direct") {
      sink.capture("buylist_viewed", { shop_slug: shopSlug, source });
    },
    /** The scanned card is in this vendor's public book, TRO-483's intended landing. */
    scanLanded(shopSlug: string) {
      sink.capture("shop_scan_landed", { shop_slug: shopSlug });
    },
    /** The scanned card is not showing. Never emitted for a narrowing the customer chose: the
     * page's own `scannedOnly` guard already distinguishes "the scan is the only reason this list
     * is empty" from a search or StockGroup filter that missed, and blaming a sale for the
     * customer's own filter would give a wrong answer, not just a noisy one. */
    scanMissed(shopSlug: string, cause: ScanMissCause) {
      sink.capture("shop_scan_missed", { shop_slug: shopSlug, cause });
    },
    /** "See everything", the designed way out of a one-card view. Counted on its own because a high
     * rate says landing on the single scanned card is not what customers want, a product answer no
     * server-side redirect log could give. */
    widened(shopSlug: string, hadScan: boolean) {
      sink.capture("shop_widened", { shop_slug: shopSlug, had_scan: hadScan });
    },
  };
}

/**
 * PostHog init config for the shop surface.
 *
 * Takes no country and reads no storage, unlike `getShowAnalyticsConfig`. That is deliberate:
 * ADR-0076 keeps this surface memory-only for every visitor, so there is nothing to branch on and
 * no EEA path to get wrong. A fresh id per page load is the whole identity model.
 */
export function getShopAnalyticsConfig(createId: () => string) {
  return {
    autocapture: false,
    bootstrap: { distinctID: createId() },
    capture_pageleave: false,
    capture_pageview: false,
    disable_session_recording: true,
    persistence: "memory" as const,
    person_profiles: "never" as const,
  };
}
